import { Controller } from '@hotwired/stimulus'

const MAX_IMAGES = 3

export default class extends Controller {
  static values = { i18n: Object }

  static targets = [
    'title', 'category', 'brand', 'model', 'description', 'condition',
    'priceMode', 'priceInput', 'priceCents', 'pricePanel', 'openPricePanel',
    'municipality', 'neighborhood', 'imageInput', 'photoSlot', 'retainedImage',
    'previewTitle', 'previewCategory', 'previewDescription', 'previewPrice',
    'previewCondition', 'previewPlace'
  ]

  connect() {
    this.imageEntries = this.photoSlotTargets.filter((slot) => slot.dataset.existingImageId).map((slot) => ({
      type: 'existing',
      id: slot.dataset.existingImageId,
      src: slot.dataset.existingImageSrc,
      name: slot.dataset.existingImageName
    }))
    Array.from(this.imageInputTarget.files).forEach((file) => this.imageEntries.push({ type: 'new', file }))
    this.objectUrls = []
    this.priceModeChanged()
    this.updatePreview()
  }

  disconnect() { this.revokeObjectUrls() }

  updatePreview() {
    const title = this.titleTarget.value.trim()
    const brandModel = [this.brandTarget.value.trim(), this.modelTarget.value.trim()].filter(Boolean).join(' ')
    const description = this.descriptionTarget.value.trim()

    this.previewTitleTarget.textContent = title || brandModel || this.i18nValue.preview_title
    this.previewTitleTarget.classList.toggle('is-placeholder', !title && !brandModel)
    this.previewDescriptionTarget.textContent = description || brandModel || this.i18nValue.preview_body
    this.previewCategoryTarget.textContent = this.selectedOptionText(this.categoryTarget) || this.i18nValue.preview_category
    this.previewConditionTarget.textContent = this.selectedConditionText() || this.i18nValue.preview_condition
    const city = this.selectedOptionText(this.municipalityTarget).split(',')[0]
    this.previewPlaceTarget.textContent = [this.neighborhoodTarget.value.trim(), city].filter(Boolean).join(', ') || this.i18nValue.default_municipality
    this.updatePreviewPrice()
  }

  priceModeChanged() {
    const open = this.currentPriceMode() === 'open'
    this.pricePanelTarget.hidden = open
    this.openPricePanelTarget.hidden = !open
    this.priceInputTarget.required = !open
    if (open) this.priceCentsTarget.value = ''
    else this.syncPrice()
    this.updatePreviewPrice()
  }

  priceChanged() {
    const digits = this.priceInputTarget.value.replace(/\D/g, '')
    this.priceInputTarget.value = digits ? Number(digits).toLocaleString('pt-BR') : ''
    this.syncPrice()
    this.updatePreviewPrice()
  }

  submit() { if (this.currentPriceMode() === 'fixed') this.syncPrice() }

  chooseImages(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (this.imageEntries[index]) {
      this.imageEntries.splice(index, 1)
      this.syncImageInputs()
      this.renderPhotoSlots()
    } else {
      this.imageInputTarget.click()
    }
  }

  imagesChanged() {
    const additions = Array.from(this.imageInputTarget.files)
    const newFiles = this.imageEntries.filter((entry) => entry.type === 'new').map((entry) => entry.file)
    const known = new Set(newFiles.map((file) => this.fileKey(file)))
    const unique = additions.filter((file) => !known.has(this.fileKey(file)))
    const available = MAX_IMAGES - this.imageEntries.length
    unique.slice(0, available).forEach((file) => this.imageEntries.push({ type: 'new', file }))
    if (unique.length > available) window.alert(this.i18nValue.maximum_images)
    this.syncImageInputs()
    this.renderPhotoSlots()
  }

  syncPrice() {
    const digits = this.priceInputTarget.value.replace(/\D/g, '')
    this.priceCentsTarget.value = digits && Number.isSafeInteger(Number(digits) * 100) ? String(Number(digits) * 100) : ''
  }

  updatePreviewPrice() {
    if (this.currentPriceMode() === 'open') {
      this.previewPriceTarget.textContent = this.i18nValue.open_price
    } else {
      const amount = this.priceInputTarget.value.trim()
      this.previewPriceTarget.textContent = amount
        ? this.interpolate(this.i18nValue.preview_price, { AMOUNT: amount })
        : this.i18nValue.preview_price_empty
    }
  }

  currentPriceMode() { return this.priceModeTargets.find((radio) => radio.checked)?.value || 'fixed' }

  selectedOptionText(select) { return select.selectedOptions[0]?.value ? select.selectedOptions[0].textContent.trim() : '' }

  selectedConditionText() { return this.conditionTargets.find((input) => input.checked)?.dataset.label || '' }

  syncImageInputs() {
    const retainedIds = new Set(
      this.imageEntries.filter((entry) => entry.type === 'existing').map((entry) => entry.id)
    )
    this.retainedImageTargets.forEach((input) => { input.disabled = !retainedIds.has(input.dataset.imageId) })

    if (typeof DataTransfer === 'undefined') return
    const transfer = new DataTransfer()
    this.imageEntries.filter((entry) => entry.type === 'new').forEach((entry) => transfer.items.add(entry.file))
    this.imageInputTarget.files = transfer.files
  }

  renderPhotoSlots() {
    this.revokeObjectUrls()
    this.photoSlotTargets.forEach((slot, index) => {
      const entry = this.imageEntries[index]
      slot.replaceChildren()
      this.syncExistingImageData(slot, entry)
      if (entry) {
        const image = document.createElement('img')
        image.src = entry.type === 'existing' ? entry.src : this.objectUrl(entry.file)
        image.alt = ''
        const remove = document.createElement('span')
        remove.className = 'request-photo-slot__remove'
        remove.textContent = this.i18nValue.remove
        slot.append(image, remove)
        slot.classList.add('has-image')
        slot.setAttribute('aria-label', this.interpolate(this.i18nValue.remove_image, {
          NUMBER: index + 1,
          FILENAME: entry.type === 'existing' ? entry.name : entry.file.name
        }))
      } else {
        slot.append(this.imageIcon(), this.addLabel())
        slot.classList.remove('has-image')
        slot.setAttribute('aria-label', this.interpolate(this.i18nValue.add_image, { NUMBER: index + 1 }))
      }
    })
  }

  objectUrl(file) {
    const url = URL.createObjectURL(file)
    this.objectUrls.push(url)
    return url
  }

  syncExistingImageData(slot, entry) {
    if (entry?.type === 'existing') {
      slot.dataset.existingImageId = entry.id
      slot.dataset.existingImageSrc = entry.src
      slot.dataset.existingImageName = entry.name
    } else {
      delete slot.dataset.existingImageId
      delete slot.dataset.existingImageSrc
      delete slot.dataset.existingImageName
    }
  }

  imageIcon() {
    const namespace = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(namespace, 'svg')
    svg.setAttribute('width', '22')
    svg.setAttribute('height', '22')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('aria-hidden', 'true')
    svg.innerHTML = '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9" r="1.6"/><path d="m21 16-4.5-4.5L7 21"/>'
    return svg
  }

  addLabel() {
    const label = document.createElement('span')
    label.textContent = this.i18nValue.add
    return label
  }

  interpolate(template, values) {
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`__${key}__`, value),
      template
    )
  }

  fileKey(file) { return `${file.name}:${file.size}:${file.lastModified}` }

  revokeObjectUrls() {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls = []
  }
}
